import dayjs, { type Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  type DeliveryAttempt,
  ScheduleType,
  SlackDeliveryTarget,
  TeamsDeliveryTarget,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const DAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_LABELS_LONG = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
const WEEKDAYS = [1, 2, 3, 4, 5];

enum Frequency {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

type FrequencyState = {
  frequency: Frequency;
  hour: number;
  daysOfWeek: number[];
  dayOfMonth: number;
  timezone: string;
};

/**
 * Parses a BE timestamp as UTC and returns a local-timezone dayjs.
 * Tolerates both Z-suffixed and naive ISO strings — both are interpreted as UTC.
 *
 * @param {string} timestamp - BE timestamp to interpret as UTC.
 * @returns {Dayjs} Local-timezone Day.js value.
 */
function parseUtcToLocal(timestamp: string): Dayjs {
  return dayjs.utc(timestamp).local();
}

/**
 * Formats a BE timestamp as a relative string (e.g. "5 hours ago"), interpreting
 * the input as UTC regardless of whether it carries a timezone marker.
 *
 * @param {string} timestamp - BE timestamp to format.
 * @returns {string} Relative local-time string.
 */
function formatRunTimeRelative(timestamp: string): string {
  return parseUtcToLocal(timestamp).fromNow();
}

/**
 * Formats a BE timestamp using a Day.js format string, in the user's local timezone.
 *
 * @param {string} timestamp - BE timestamp to format.
 * @param {string} format - Day.js format string.
 * @returns {string} Formatted local-time string.
 */
function formatRunTimeAbsolute(timestamp: string, format: string): string {
  return parseUtcToLocal(timestamp).format(format);
}

/**
 * Returns the user's IANA timezone via dayjs.tz.guess(), with a UTC fallback.
 *
 * @returns {string} Browser timezone name, or UTC when unavailable.
 */
function getDefaultTimezone(): string {
  const guessed = dayjs.tz.guess();
  return guessed || 'UTC';
}

/**
 * Returns a fresh FrequencyState seeded with sensible defaults: weekly Monday 9am
 * in the user's browser timezone.
 *
 * @returns {FrequencyState} Default frequency state for a new schedule.
 */
function getDefaultFrequencyState(): FrequencyState {
  return {
    frequency: Frequency.WEEKLY,
    hour: 9,
    daysOfWeek: [1],
    dayOfMonth: 1,
    timezone: getDefaultTimezone(),
  };
}

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: Frequency.DAILY, label: 'Every day' },
  { value: Frequency.WEEKLY, label: 'Weekly' },
  { value: Frequency.MONTHLY, label: 'Monthly' },
];

const HOUR_OPTIONS: { value: string; label: string }[] = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: formatHour12(hour),
}));

/**
 * Builds the timezone Select options list. Pulls from Intl.supportedValuesOf when
 * available, falling back to a UTC-only list, and prepends UTC if the runtime list
 * doesn't include it.
 *
 * @returns {{ value: string; label: string }[]} Timezone options ordered for display.
 */
function buildTimezoneOptions(): { value: string; label: string }[] {
  const supported = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  for (const tz of supported.length > 0 ? supported : ['UTC']) {
    if (seen.has(tz)) continue;
    seen.add(tz);
    options.push({ value: tz, label: `${tz} (${formatTimezoneAbbreviation(tz)})` });
  }
  if (!seen.has('UTC')) {
    options.unshift({ value: 'UTC', label: 'UTC' });
  }
  return options;
}

const TIMEZONE_OPTIONS = buildTimezoneOptions();

/**
 * Build a UTC 5-field cron expression from a FrequencyState describing a schedule
 * in the state's local timezone. Hour and day-of-week roll across timezone offsets:
 * "Mondays at 1 AM in Asia/Tokyo" becomes Sunday 16:00 UTC.
 *
 * @param {FrequencyState} state - Local schedule frequency state.
 * @returns {string} UTC 5-field cron expression.
 */
function buildCronFromFrequency(state: FrequencyState): string {
  const { frequency, hour, daysOfWeek, dayOfMonth, timezone: tz } = state;

  if (frequency === Frequency.DAILY) {
    const utcHour = convertLocalHourToUtc(hour, tz);
    return `0 ${utcHour} * * *`;
  }

  if (frequency === Frequency.MONTHLY) {
    const utcHour = convertLocalHourToUtc(hour, tz);
    return `0 ${utcHour} ${dayOfMonth} * *`;
  }

  const days = daysOfWeek.length > 0 ? daysOfWeek : [1];
  const utcSlots = new Set<string>();
  for (const dow of days) {
    const local = nextOccurrenceLocal(dow, hour, tz);
    const asUtc = local.utc();
    utcSlots.add(`${asUtc.hour()}|${asUtc.day()}`);
  }
  const slotsByHour = new Map<number, Set<number>>();
  for (const slot of utcSlots) {
    const [h, d] = slot.split('|').map(Number);
    if (!slotsByHour.has(h)) slotsByHour.set(h, new Set());
    slotsByHour.get(h)!.add(d);
  }

  if (slotsByHour.size === 1) {
    const [utcHour, utcDays] = [...slotsByHour.entries()][0];
    return `0 ${utcHour} * * ${formatDayList(utcDays)}`;
  }

  // Mixed UTC hours (rare: only when picked-day-set straddles a date-line shift).
  // Fall back to a comma-joined OR-shape: "0 H1,H2 * * D1,D2". Cron evaluates
  // (hour AND dow) for each row, so this overschedules — surface a warning.
  const allHours = [...slotsByHour.keys()].sort((a, b) => a - b).join(',');
  const allDays = formatDayList(new Set([...slotsByHour.values()].flatMap((days) => [...days])));
  return `0 ${allHours} * * ${allDays}`;
}

/**
 * Best-effort parse of a UTC cron into a FrequencyState in the supplied display tz.
 * Returns null when the cron doesn't match one of the three supported shapes
 * (Daily `0 H * * *`, Weekly `0 H * * D[,D...]`, Monthly `0 H N * *`); the caller
 * should fall back to advanced/custom mode in that case.
 *
 * @param {string | null} cron - UTC cron expression to parse.
 * @param {string} displayTimezone - IANA timezone used for local display fields.
 * @returns {FrequencyState | null} Parsed frequency state, or null for unsupported cron shapes.
 */
function parseCronToFrequency(cron: string | null, displayTimezone: string): FrequencyState | null {
  if (!cron) return null;
  const parts = cron.trim().replace(/\s+/g, ' ').split(' ');
  if (parts.length !== 5) return null;
  const [minute, hourField, domField, monthField, dowField] = parts;
  if (minute !== '0') return null;
  if (monthField !== '*') return null;

  const hour = parseSingleInt(hourField, 0, 23);
  if (hour === null) return null;

  if (domField === '*' && dowField === '*') {
    const local = utcSlotToLocal(hour, 0, displayTimezone);
    return {
      frequency: Frequency.DAILY,
      hour: local.hour,
      daysOfWeek: [],
      dayOfMonth: 1,
      timezone: displayTimezone,
    };
  }

  if (dowField === '*') {
    const dom = parseSingleInt(domField, 1, 28);
    if (dom === null) return null;
    const local = utcSlotToLocal(hour, 0, displayTimezone);
    return {
      frequency: Frequency.MONTHLY,
      hour: local.hour,
      daysOfWeek: [],
      dayOfMonth: dom,
      timezone: displayTimezone,
    };
  }

  if (domField !== '*') return null;
  const dows = parseDayList(dowField);
  if (dows === null) return null;
  const localHours = new Set<number>();
  const localDays = new Set<number>();
  for (const dow of dows) {
    const local = utcSlotToLocal(hour, dow, displayTimezone);
    localHours.add(local.hour);
    localDays.add(local.dow);
  }
  if (localHours.size !== 1) return null;
  return {
    frequency: Frequency.WEEKLY,
    hour: [...localHours][0],
    daysOfWeek: [...localDays].sort((a, b) => a - b),
    dayOfMonth: 1,
    timezone: displayTimezone,
  };
}

/**
 * Validates a 5-field cron expression with hour granularity: minute must be `0`.
 * Defers semantic validation to the server; this is a fast pre-submit gate.
 *
 * @param {string} expression - Cron expression to validate.
 * @returns {boolean} True when the expression is a 5-field hourly cron.
 */
function isValidHourCron(expression: string): boolean {
  const trimmed = expression.trim();
  if (!trimmed) return false;
  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return false;
  return parts[0] === '0';
}

/**
 * Returns a human-readable schedule description in the supplied display timezone.
 * Falls back to the raw cron expression for schedules that don't match the three
 * frequency-builder shapes.
 *
 * @param {ScheduleType} scheduleType - Schedule type returned by the API.
 * @param {string | null} cron - UTC cron expression, when present.
 * @param {string} displayTimezone - IANA timezone used for display.
 * @returns {string} Human-readable schedule description.
 */
function humanizeSchedule(scheduleType: ScheduleType, cron: string | null, displayTimezone: string): string {
  if (scheduleType === ScheduleType.MANUAL) return 'Manual';
  if (!cron) return 'No schedule set';
  const state = parseCronToFrequency(cron, displayTimezone);
  if (!state) {
    return `${cron.trim().replace(/\s+/g, ' ')} (UTC)`;
  }
  const tzAbbreviation = formatTimezoneAbbreviation(displayTimezone);
  const timeLabel = formatHour12(state.hour);
  if (state.frequency === Frequency.DAILY) {
    return `Every day at ${timeLabel} ${tzAbbreviation}`;
  }
  if (state.frequency === Frequency.MONTHLY) {
    return `Day ${state.dayOfMonth} of every month at ${timeLabel} ${tzAbbreviation}`;
  }
  if (state.daysOfWeek.length === 7) {
    return `Every day at ${timeLabel} ${tzAbbreviation}`;
  }
  if (state.daysOfWeek.length === WEEKDAYS.length && WEEKDAYS.every((d) => state.daysOfWeek.includes(d))) {
    return `Weekdays at ${timeLabel} ${tzAbbreviation}`;
  }
  if (state.daysOfWeek.length === 1) {
    return `${DAY_LABELS_LONG[state.daysOfWeek[0]]} at ${timeLabel} ${tzAbbreviation}`;
  }
  const days = state.daysOfWeek.map((d) => DAY_LABELS_SHORT[d]).join(', ');
  return `${days} at ${timeLabel} ${tzAbbreviation}`;
}

/**
 * Converts a local hour in the supplied timezone to the corresponding UTC hour.
 *
 * @param {number} hour - Local hour in 24-hour format.
 * @param {string} tz - IANA timezone for the local hour.
 * @returns {number} UTC hour in 24-hour format.
 */
function convertLocalHourToUtc(hour: number, tz: string): number {
  const local = dayjs.tz(`${todayDateOnly()} ${pad2(hour)}:00`, tz);
  return local.utc().hour();
}

/**
 * Finds the next local occurrence for a weekday and hour in a timezone.
 *
 * @param {number} dow - Day of week where Sunday is 0.
 * @param {number} hour - Local hour in 24-hour format.
 * @param {string} tz - IANA timezone for the occurrence.
 * @returns {Dayjs} Local Day.js occurrence.
 */
function nextOccurrenceLocal(dow: number, hour: number, tz: string): Dayjs {
  const base = dayjs.tz(`${todayDateOnly()} ${pad2(hour)}:00`, tz);
  const diff = (dow - base.day() + 7) % 7;
  return base.add(diff, 'day');
}

/**
 * Converts a UTC weekday/hour slot into local display weekday/hour fields.
 *
 * @param {number} utcHour - UTC hour in 24-hour format.
 * @param {number} utcDow - UTC day of week where Sunday is 0.
 * @param {string} tz - IANA timezone used for local conversion.
 * @returns {{ hour: number; dow: number }} Local hour and day-of-week fields.
 */
function utcSlotToLocal(utcHour: number, utcDow: number, tz: string): { hour: number; dow: number } {
  // Pick a Sunday in 2026-05 as a stable anchor (no DST seam in May for most US tz).
  const anchorSunday = dayjs.utc('2026-05-03 00:00');
  const utcMoment = anchorSunday.add(utcDow, 'day').add(utcHour, 'hour');
  const local = utcMoment.tz(tz);
  return { hour: local.hour(), dow: local.day() };
}

/**
 * Formats a set of numeric day-of-week values for a cron field.
 *
 * @param {Set<number>} days - Day-of-week values to format.
 * @returns {string} Sorted comma-delimited day list.
 */
function formatDayList(days: Set<number>): string {
  return [...days].sort((a, b) => a - b).join(',');
}

/**
 * Parses a bounded integer field from a cron expression.
 *
 * @param {string} field - Cron field to parse.
 * @param {number} min - Minimum allowed value.
 * @param {number} max - Maximum allowed value.
 * @returns {number | null} Parsed integer, or null when invalid/out of range.
 */
function parseSingleInt(field: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(field)) return null;
  const value = Number(field);
  if (value < min || value > max) return null;
  return value;
}

/**
 * Parses a cron day-of-week field into normalized numeric days.
 *
 * @param {string} field - Cron day-of-week field.
 * @returns {number[] | null} Parsed days where Sunday is 0, or null when unsupported.
 */
function parseDayList(field: string): number[] | null {
  if (field === '*') return null;
  const out = new Set<number>();
  for (const part of field.split(',')) {
    if (/^\d+$/.test(part)) {
      const value = Number(part);
      if (value < 0 || value > 7) return null;
      out.add(value === 7 ? 0 : value);
      continue;
    }
    const rangeMatch = part.match(/^(\d+)-(\d+)$/);
    if (!rangeMatch) return null;
    const start = Number(rangeMatch[1]);
    const end = Number(rangeMatch[2]);
    if (start < 0 || end > 7 || start > end) return null;
    for (let value = start; value <= end; value += 1) {
      out.add(value === 7 ? 0 : value);
    }
  }
  return [...out];
}

/**
 * Formats a 24-hour integer hour as a 12-hour time label.
 *
 * @param {number} hour - Hour in 24-hour format.
 * @returns {string} Time label such as "9:00 AM".
 */
function formatHour12(hour: number): string {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:00 ${suffix}`;
}

/**
 * Returns a short timezone label for display.
 *
 * @param {string} tz - IANA timezone to abbreviate.
 * @returns {string} Short timezone label, or the timezone name as a fallback.
 */
function formatTimezoneAbbreviation(tz: string): string {
  // dayjs `z` token requires the advanced-format plugin we don't load globally;
  // pull the abbreviation via Intl which is built in everywhere we run.
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' });
  const parts = formatter.formatToParts(new Date());
  const tzPart = parts.find((part) => part.type === 'timeZoneName');
  return tzPart?.value ?? tz;
}

/**
 * Formats today's date for Day.js timezone parsing.
 *
 * @returns {string} Current local date in YYYY-MM-DD format.
 */
function todayDateOnly(): string {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * Pads a numeric value to at least two characters.
 *
 * @param {number} value - Numeric value to pad.
 * @returns {string} Two-character string for values below 10.
 */
function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Formats a duration in seconds as a short human string (e.g. "4m 12s" or "842ms").
 *
 * @param {number | null} seconds - Duration in seconds.
 * @returns {string} Short duration label, or an em dash when unavailable.
 */
function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds - mins * 60);
  return `${mins}m ${secs}s`;
}

/**
 * Formats a delivery attempt's display target.
 *
 * @param {DeliveryAttempt} attempt - Delivery attempt to label.
 * @returns {string | null} Target label, or null when no target metadata is available.
 */
function getDeliveryAttemptTargetLabel(attempt: DeliveryAttempt): string | null {
  if (attempt.recipients && attempt.recipients.length > 0) {
    return attempt.recipients.join(', ');
  }
  if (attempt.channel_name) {
    return `#${attempt.channel_name}`;
  }
  // Slack DM uses target 'dm'; Teams 1:1 chat uses target 'chat' — both render as "DM".
  if (attempt.target === SlackDeliveryTarget.DM || attempt.target === TeamsDeliveryTarget.CHAT) {
    return 'DM';
  }
  // 'channel' is shared by Slack and Teams; fall back to whichever channel id is present.
  if (attempt.target === SlackDeliveryTarget.CHANNEL && (attempt.slack_channel_id || attempt.channel_id)) {
    return attempt.slack_channel_id ?? attempt.channel_id ?? null;
  }
  return null;
}

export {
  buildCronFromFrequency,
  DAY_LABELS_SHORT,
  formatDuration,
  formatRunTimeAbsolute,
  formatRunTimeRelative,
  Frequency,
  FREQUENCY_OPTIONS,
  getDefaultFrequencyState,
  getDeliveryAttemptTargetLabel,
  HOUR_OPTIONS,
  humanizeSchedule,
  isValidHourCron,
  parseCronToFrequency,
  parseUtcToLocal,
  TIMEZONE_OPTIONS,
};

export type { FrequencyState };
